import { useState } from 'react';
import { ChevronRight, Edit2, User, Phone, Mail, CheckCircle, Plus } from 'lucide-react';
import { LoginRequiredCard } from './LoginRequiredCard';
import { EditNameModal } from './profile/EditNameModal';
import { EditPhoneScreen } from './profile/EditPhoneScreen';
import { EditEmailScreen } from './profile/EditEmailScreen';
import { PassengerModal } from './profile/PassengerModal';
import { ConfirmDialog } from './profile/ConfirmDialog';
import { Toast } from './profile/Toast';
import { PassengerData } from './profile/PassengerForm';
import { AccountLayout } from './shared/AccountLayout';
import { useAuth } from '../hooks/useAuth';
import { useNavigation } from '../hooks/useNavigation';

type EditScreen = 'profile' | 'phone' | 'email';

const ProfileHeaderCard = ({ 
  userName, 
  userEmail,
  onEdit
}: { 
  userName: string; 
  userEmail: string;
  onEdit?: () => void;
}) => {
  return (
    <div className="bg-white rounded-[16px] border border-[#E5E7EB] p-6 shadow-sm">
      <div className="flex items-start gap-4">
        {/* Avatar */}
        <div className="w-16 h-16 bg-gradient-to-br from-[#FF6A2A] to-[#C2410C] rounded-full flex items-center justify-center flex-shrink-0">
          <span className="text-white font-bold text-2xl">A</span>
        </div>

        {/* User Info */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <h2 className="font-bold text-[#111827] text-lg truncate">{userName}</h2>
            <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-[#ECFDF5] text-[#10B981] text-xs font-semibold rounded-[6px]">
              <CheckCircle className="w-3 h-3" />
              Verificado
            </span>
          </div>
          <p className="text-sm text-[#6B7280] truncate">{userEmail}</p>
        </div>

        {/* Edit Button */}
        <button 
          onClick={onEdit}
          className="p-2 hover:bg-[#F7F8FA] rounded-[10px] transition-colors flex-shrink-0"
        >
          <Edit2 className="w-5 h-5 text-[#6B7280]" />
        </button>
      </div>
    </div>
  );
};

const DataRow = ({ 
  icon, 
  label, 
  value,
  onClick
}: { 
  icon: React.ReactNode; 
  label: string; 
  value: string;
  onClick?: () => void;
}) => {
  return (
    <button 
      onClick={onClick}
      className="w-full flex items-center gap-3 p-4 hover:bg-[#F7F8FA] transition-colors group"
    >
      <div className="text-[#6B7280]">{icon}</div>
      <div className="flex-1 text-left">
        <p className="text-xs text-[#6B7280] mb-0.5">{label}</p>
        <p className="font-medium text-[#111827]">{value}</p>
      </div>
      <ChevronRight className="w-5 h-5 text-[#6B7280] group-hover:text-[#FF6A2A] transition-colors" />
    </button>
  );
};

const PassengerListItem = ({ 
  name, 
  type,
  onClick
}: { 
  name: string; 
  type: 'Adulto' | 'Niño';
  onClick?: () => void;
}) => {
  return (
    <button 
      onClick={onClick}
      className="w-full flex items-center justify-between p-4 hover:bg-[#F7F8FA] transition-colors"
    >
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 bg-[#F7F8FA] rounded-full flex items-center justify-center">
          <User className="w-5 h-5 text-[#6B7280]" />
        </div>
        <div>
          <p className="font-medium text-[#111827]">{name}</p>
          <span className="inline-block px-2 py-0.5 bg-[#F7F8FA] text-[#6B7280] text-xs font-medium rounded-[6px] mt-1">
            {type}
          </span>
        </div>
      </div>
      <ChevronRight className="w-5 h-5 text-[#6B7280]" />
    </button>
  );
};

export const ProfileScreen = () => {
  // Use hooks for state and navigation
  const { isLoggedIn } = useAuth();
  const { goBack, goToLogin, goToSettings, goToHome, goToAccount, goToMyTickets } = useNavigation();

  // Current screen state
  const [currentScreen, setCurrentScreen] = useState<EditScreen>('profile');

  // User data state
  const [firstName, setFirstName] = useState('Ana María');
  const [lastName, setLastName] = useState('García López');
  const [phone, setPhone] = useState('+52 55 1234 5678');
  const [email, setEmail] = useState('ana.garcia@example.com');

  // Passengers state
  const [passengers, setPassengers] = useState<(PassengerData & { id: string })[]>([
    { id: '1', firstName: 'Carlos', lastName: 'García Ruiz', type: 'Adulto' },
    { id: '2', firstName: 'María', lastName: 'García Ruiz', type: 'Niño' }
  ]);

  // Modal states
  const [editNameModal, setEditNameModal] = useState<{ isOpen: boolean; field: 'firstName' | 'lastName' | null }>({
    isOpen: false,
    field: null
  });
  const [passengerModal, setPassengerModal] = useState<{
    isOpen: boolean;
    mode: 'add' | 'edit';
    data?: PassengerData & { id: string };
  }>({
    isOpen: false,
    mode: 'add'
  });
  const [deleteDialog, setDeleteDialog] = useState<{ isOpen: boolean; passengerId: string | null }>({
    isOpen: false,
    passengerId: null
  });

  // Toast state
  const [toast, setToast] = useState({ isVisible: false, message: '', type: 'success' as 'success' | 'error' | 'info' });

  // Handlers
  const showToast = (message: string, type: 'success' | 'error' | 'info' = 'success') => {
    setToast({ isVisible: true, message, type });
  };

  const handleSaveFirstName = (value: string) => {
    setFirstName(value);
    showToast('Nombre actualizado correctamente');
  };

  const handleSaveLastName = (value: string) => {
    setLastName(value);
    showToast('Apellido actualizado correctamente');
  };

  const handleSavePhone = (value: string) => {
    setPhone(value);
    setCurrentScreen('profile');
    showToast('Teléfono actualizado correctamente');
  };

  const handleSaveEmail = (value: string) => {
    setEmail(value);
    setCurrentScreen('profile');
    showToast('Correo actualizado correctamente');
  };

  const handleSavePassenger = (passenger: PassengerData) => {
    if (passengerModal.mode === 'add') {
      const newPassenger = { ...passenger, id: Date.now().toString() };
      setPassengers([...passengers, newPassenger]);
      showToast('Pasajero agregado correctamente');
    } else if (passengerModal.data) {
      setPassengers(passengers.map(p => 
        p.id === passengerModal.data!.id ? { ...passenger, id: p.id } : p
      ));
      showToast('Pasajero actualizado correctamente');
    }
    setPassengerModal({ isOpen: false, mode: 'add' });
  };

  const handleDeletePassenger = () => {
    if (deleteDialog.passengerId) {
      setPassengers(passengers.filter(p => p.id !== deleteDialog.passengerId));
      showToast('Pasajero eliminado correctamente');
      setDeleteDialog({ isOpen: false, passengerId: null });
      setPassengerModal({ isOpen: false, mode: 'add' });
    }
  };

  // Render different screens
  if (currentScreen === 'phone') {
    return (
      <EditPhoneScreen
        onBack={() => setCurrentScreen('profile')}
        onSave={handleSavePhone}
        currentPhone={phone}
      />
    );
  }

  if (currentScreen === 'email') {
    return (
      <EditEmailScreen
        onBack={() => setCurrentScreen('profile')}
        onSave={handleSaveEmail}
        currentEmail={email}
      />
    );
  }

  return (
    <AccountLayout
      title="Mi perfil"
      subtitle="Administra tu información personal y pasajeros"
      activePage="account"
      onBack={goBack}
      onHomeClick={goToHome}
      onMyTripsClick={goToAccount}
      onAccountClick={goToAccount}
      onProfileClick={() => {}} // Ya estamos en perfil
      onMyTicketsClick={goToMyTickets}
      onSettingsClick={goToSettings}
      onLogoutClick={goBack}
      isLoggedIn={isLoggedIn}
      onLoginClick={goToLogin}
    >
      {/* Show Login Required Card if not logged in */}
      {!isLoggedIn && goToLogin ? (
        <LoginRequiredCard 
          onLoginClick={goToLogin}
        />
      ) : (
        <div className="space-y-4">
          {/* Profile Header Card */}
          <ProfileHeaderCard 
            userName={`${firstName} ${lastName}`}
            userEmail={email}
            onEdit={() => setEditNameModal({ isOpen: true, field: 'firstName' })}
          />

          {/* Personal Data Section */}
          <div className="bg-white rounded-[16px] border border-[#E5E7EB] shadow-sm overflow-hidden">
            <div className="p-4 border-b border-[#E5E7EB]">
              <h3 className="font-bold text-[#111827]">Datos personales</h3>
            </div>
            <div className="divide-y divide-[#E5E7EB]">
              <DataRow
                icon={<User className="w-5 h-5" />}
                label="Nombre(s)"
                value={firstName}
                onClick={() => setEditNameModal({ isOpen: true, field: 'firstName' })}
              />
              <DataRow
                icon={<User className="w-5 h-5" />}
                label="Apellido paterno"
                value={lastName}
                onClick={() => setEditNameModal({ isOpen: true, field: 'lastName' })}
              />
              <DataRow
                icon={<Phone className="w-5 h-5" />}
                label="Teléfono"
                value={phone}
                onClick={() => setCurrentScreen('phone')}
              />
              <DataRow
                icon={<Mail className="w-5 h-5" />}
                label="Correo electrónico"
                value={email}
                onClick={() => setCurrentScreen('email')}
              />
            </div>
          </div>

          {/* Saved Passengers Section */}
          <div className="bg-white rounded-[16px] border border-[#E5E7EB] shadow-sm overflow-hidden">
            <div className="p-4 border-b border-[#E5E7EB]">
              <h3 className="font-bold text-[#111827]">Pasajeros guardados</h3>
              <p className="text-xs text-[#6B7280] mt-1">Facilita futuras reservas</p>
            </div>
            <div className="divide-y divide-[#E5E7EB]">
              {passengers.map(passenger => (
                <PassengerListItem 
                  key={passenger.id}
                  name={`${passenger.firstName} ${passenger.lastName}`}
                  type={passenger.type}
                  onClick={() => setPassengerModal({ isOpen: true, mode: 'edit', data: passenger })}
                />
              ))}
            </div>
            <div className="p-4 border-t border-[#E5E7EB]">
              <button 
                onClick={() => setPassengerModal({ isOpen: true, mode: 'add' })}
                className="w-full flex items-center justify-center gap-2 py-3 border-2 border-dashed border-[#E5E7EB] text-[#6B7280] font-medium rounded-[12px] hover:border-[#FF6A2A] hover:text-[#FF6A2A] hover:bg-[#FFF5F0] transition-colors"
              >
                <Plus className="w-5 h-5" />
                Agregar pasajero
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modals */}
      {editNameModal.field && (
        <EditNameModal
          isOpen={editNameModal.isOpen}
          onClose={() => setEditNameModal({ isOpen: false, field: null })}
          onSave={editNameModal.field === 'firstName' ? handleSaveFirstName : handleSaveLastName}
          currentValue={editNameModal.field === 'firstName' ? firstName : lastName}
          field={editNameModal.field}
        />
      )}

      <PassengerModal
        isOpen={passengerModal.isOpen}
        onClose={() => setPassengerModal({ isOpen: false, mode: 'add' })}
        onSave={handleSavePassenger}
        onDelete={passengerModal.mode === 'edit' ? () => setDeleteDialog({ isOpen: true, passengerId: passengerModal.data?.id || null }) : undefined}
        initialData={passengerModal.data}
        mode={passengerModal.mode}
      />

      <ConfirmDialog
        isOpen={deleteDialog.isOpen}
        onClose={() => setDeleteDialog({ isOpen: false, passengerId: null })}
        onConfirm={handleDeletePassenger}
        title="Eliminar pasajero"
        message="Esta acción no se puede deshacer. ¿Estás seguro de que deseas eliminar este pasajero?"
        confirmLabel="Eliminar"
        cancelLabel="Cancelar"
        variant="danger"
      />

      <Toast
        isVisible={toast.isVisible}
        message={toast.message}
        type={toast.type}
        onClose={() => setToast({ ...toast, isVisible: false })}
      />
    </AccountLayout>
  );
};

export default ProfileScreen;